import React from 'react';
import Icon from '@material-ui/core/Icon';
import Link from '@material-ui/core/Link';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { withTranslation, WithTranslation } from 'react-i18next';
// Components
import EnhancedListItem from '../../../components/listItem';
// Modules
import { BookingModule } from '../../../redux/reducers/api-types';
// Ressources
import envConfig from '../../../config';

interface BookingBillProps extends WithTranslation {
    title?: string;
    booking: BookingModule;
}

const BookingBill = (props: BookingBillProps): JSX.Element => {
    const classes = useStyles();
    const { t, booking, title: customTitle } = props;
    const { ride_billing, bill_billing, products_billing, refunds_billing, tolls } = booking;

    const detailsFee = ride_billing?.price_explain;
    const { transport: transportFee, ...othersFee } = products_billing || {};

    const driverRideComRaw = booking.driver_ride_com ?? ride_billing?.price_details?.driver_ride_com;

    const driverRideComNumber =
        driverRideComRaw !== undefined && driverRideComRaw !== null && !Number.isNaN(Number(driverRideComRaw))
            ? Number(driverRideComRaw)
            : undefined;

    const billingCell = {
        title: t('billing'),
        data: (
            <div>
                {booking?.paiement_mode && (
                    <div>
                        <strong>{t('paiement_mode')} :</strong> {booking?.paiement_mode}
                    </div>
                )}
                {bill_billing && (
                    <div>
                        <strong>{t('common:form.number')} :</strong> {bill_billing.bill_number}
                    </div>
                )}
                {bill_billing && (
                    <div>
                        <strong>{t('amount')} :</strong>{' '}
                        {`${bill_billing?.amount_pre_tax} € HT (${bill_billing.amount_with_tax} € TTC)`}
                    </div>
                )}
                {refunds_billing?.length > 0 && (
                    <>
                        <div>{<strong>{t('refunds')}</strong>}</div>
                        {refunds_billing.map((refound) => (
                            <div key={`${refound?.amount_pre_tax}${refound?.bill_number}`}>
                                {`${refound?.amount_pre_tax} € HT (${refound.amount_with_tax} € TTC) (${refound?.bill_number})`}
                            </div>
                        ))}
                    </>
                )}
                {bill_billing && (
                    <div>
                        <strong>{t('common:table.paid')} :</strong>{' '}
                        {bill_billing.status === 'paid' ? t('common:true') : t('common:false')}
                    </div>
                )}
                {ride_billing?.ride_note_path && (
                    <>
                        <div>
                            <strong>{t('ride_note_path')} :</strong>{' '}
                            <Link color="secondary" href={ride_billing.ride_note_path}>
                                {t('common:download')}
                            </Link>
                        </div>
                    </>
                )}
                {bill_billing?.document_path && !bill_billing?.details?.no_pdf && (
                    <>
                        <div>
                            <strong>{t('pdf_invoice_url')} :</strong>{' '}
                            <Link color="secondary" href={bill_billing.document_path}>
                                {t('common:download')}
                            </Link>
                        </div>
                    </>
                )}
            </div>
        ),
    };

    // const tripCell = {
    //     title: t('trip'),
    //     data: ride_billing?.price_details?.details?.price
    //         ? `${ride_billing?.price_details?.details?.price} € TTC`
    //         : '',
    // };

    function renderPriceCell(d) {
        return (
            <div key={`${d.name}${d.price}`} style={d.is_applied === false ? { opacity: 0.6 } : {}}>
                <strong>{d.name} :</strong> {d.value}
                {d.details && <div className={classes.priceDetails}>{d.details.join(', ')}</div>}
            </div>
        );
    }

    const otherFeesRider =
        othersFee && Object.entries(othersFee).filter(([key, fee]: [any, any]) => fee.pre_tax_customer);
    const otherFeesDriver =
        othersFee && Object.entries(othersFee).filter(([key, fee]: [any, any]) => fee.pre_tax_partner);

    const detailsRiderCell = {
        title: t('rider_price'),
        data: (
            <div>
                {detailsFee && detailsFee.rider.map(renderPriceCell)}
                <hr />
                {transportFee && (
                    <div>
                        <strong>{t('transport')} :</strong> {transportFee?.pre_tax_customer} € HT (
                        {transportFee?.with_tax_customer} € TTC)
                    </div>
                )}
                {otherFeesRider?.length > 0 && (
                    <>
                        {otherFeesRider.map(([key, fee]: [any, any]) => (
                            <div key={`${key}`}>
                                <strong>{fee?.label ?? key} :</strong> {fee?.pre_tax_customer} € HT (
                                {fee?.with_tax_customer} € TTC)
                            </div>
                        ))}
                    </>
                )}
                <div>
                    <strong>{t('total_price')} :</strong> {ride_billing?.pre_tax_customer} € HT (
                    {ride_billing?.with_tax_customer} € TTC)
                </div>
            </div>
        ),
    };

    const detailsDriverCell = {
        title: t('driver_price'),
        data: (
            <div style={{ fontSize: '0.9em' }}>
                {detailsFee && detailsFee.driver.map(renderPriceCell)}
                {driverRideComNumber !== undefined && (
                    <div>
                        <strong>{t('driver_ride_com')} :</strong>{' '}
                        {driverRideComNumber !== 0 ? t('common:true') : t('common:false')}
                        <div className={classes.priceDetails}>{(driverRideComNumber * 100).toFixed(4)} %</div>
                    </div>
                )}
                <hr />
                {transportFee && (
                    <div>
                        <strong>{t('transport')} :</strong> {transportFee?.pre_tax_partner} € HT (
                        {transportFee?.with_tax_partner} € TTC)
                    </div>
                )}
                {otherFeesDriver?.length > 0 && (
                    <>
                        {otherFeesDriver.map(([key, fee]: [any, any]) => (
                            <div key={`${key}`}>
                                <strong>{fee?.label ?? key} :</strong> {fee?.pre_tax_partner} € HT (
                                {fee?.with_tax_partner} € TTC)
                            </div>
                        ))}
                    </>
                )}
                <div>
                    <strong>{t('total_price')} :</strong> {ride_billing?.pre_tax_partner} € HT (
                    {ride_billing?.with_tax_partner} € TTC)
                </div>
            </div>
        ),
    };

    // Toll details
    const tollDetailsCell = tolls?.length > 0 && {
        title: t('toll'),
        data: tolls.map((toll) => (
            <div key={`${toll.amount}${toll.lat}${toll.lng}`} style={{ display: 'flex' }}>
                <Link
                    color="secondary"
                    href={`${envConfig.aws_url}${toll.receipt}`}
                    target="_blank"
                    rel="noreferrer noopener"
                >
                    {`${t('receipt_see')} (${toll.amount} €)`}
                </Link>
                <Link
                    color="secondary"
                    style={{ marginLeft: 8 }}
                    href={`https://www.google.com/maps?q=${toll.lat},${toll.lng}`}
                    target="_blank"
                    rel="noreferrer noopener"
                >
                    <Icon fontSize="small">room</Icon>
                </Link>
            </div>
        )),
    };

    const billingItemsBeta = [detailsRiderCell, detailsDriverCell, billingCell, tollDetailsCell].filter((c) => c);

    if (bill_billing) {
        return (
            <div className={classes.billingCard}>
                <EnhancedListItem
                    datas={billingItemsBeta}
                    title={
                        customTitle ??
                        `${t(bill_billing.type === 'ride' ? 'billing_normal' : 'billing_group')} - ${
                            bill_billing.bill_number
                        }`
                    }
                />
            </div>
        );
    } else if (ride_billing) {
        let title = ride_billing.payment_mode === 'unitary' ? t('billing_normal') : t('billing_group');

        if (ride_billing.billing_status === 'pending') {
            title = t('billing_generating');
        } else if (ride_billing.billing_status === 'deferred') {
            title = t('billing_deferred');
        }

        return (
            <div className={classes.billingCard}>
                <EnhancedListItem datas={billingItemsBeta} title={customTitle ?? title} />
            </div>
        );
    }
    return (
        <Card style={{ padding: 16, marginTop: 8 }}>
            <CardContent style={{ display: 'flex', flexWrap: 'wrap', padding: 0 }}>
                <div>{t('billing_none')}</div>
            </CardContent>
        </Card>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        billingCard: {
            '& .MuiTypography-body1 > div > div > div': {
                fontSize: '0.9em',
                '& > strong': {
                    fontWeight: 'normal',
                    color: 'black',
                },
            },
            '& hr': {
                margin: '0.4em 0',
                opacity: 0.7,
            },
        },
        priceDetails: {
            fontSize: '0.75em',
            position: 'relative',
            top: '-0.25em',
            opacity: 0.8,
        },
    }),
);

const BookingBillView = withTranslation(['booking', 'common'])(BookingBill);
export default BookingBillView;
